import { useEffect, useState } from "react";
import { useQuickEditRefresh } from "@/hooks/useQuickEditRefresh";
import { api } from "@/lib/api";
import { PageHeader, StatusPill } from "@/components/ui-extra";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Wrench, Truck, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { safeFormatDate, safeDistanceToNow } from "@/lib/dates";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";

export default function VehiclesPage() {
  const { hasPerm, isMaster } = useAuth();
  const canDelete = isMaster || hasPerm?.("inventory:delete_vehicles");
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [maintOpen, setMaintOpen] = useState(false);
  const [activeVehicle, setActiveVehicle] = useState(null);
  const [maintLogs, setMaintLogs] = useState([]);
  const [form, setForm] = useState({ name: "", registration: "", model: "", year: "", status: "operational", notes: "" });
  const [maintForm, setMaintForm] = useState({ vehicle_id: "", description: "", cost: "" });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/inventory/vehicles"); setVehicles(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useQuickEditRefresh(load);

  const submit = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      await api.post("/inventory/vehicles", { ...form, year: form.year ? parseInt(form.year) : null });
      toast.success("Vehicle added");
      setOpen(false); setForm({ name: "", registration: "", model: "", year: "", status: "operational", notes: "" });
      load();
    } catch (e) { toast.error("Failed"); } finally { setSaving(false); }
  };

  const remove = async (v) => {
    setDeletingId(v.id);
    try {
      const res = await api.delete(`/inventory/vehicles/${v.id}`);
      const casc = res.data?.deleted_maintenance_logs ?? 0;
      toast.success(`Deleted ${v.name}${casc ? ` (+ ${casc} maintenance log${casc === 1 ? "" : "s"})` : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed. you may not have permission.");
    } finally {
      setDeletingId(null);
    }
  };

  const openMaintenance = async (v) => {
    setActiveVehicle(v);
    setMaintForm({ vehicle_id: v.id, description: "", cost: "" });
    try {
      const r = await api.get(`/inventory/vehicles/${v.id}/maintenance`);
      setMaintLogs(r.data);
    } catch (e) {
      // Non-fatal: the modal still opens with an empty log list so the
      // user can add a new maintenance record. Log for diagnostics.
      console.error("Failed to load maintenance history:", e);
      setMaintLogs([]);
    }
    setMaintOpen(true);
  };
  const submitMaint = async () => {
    if (!maintForm.description) { toast.error("Description required"); return; }
    setSaving(true);
    try {
      await api.post("/inventory/vehicles/maintenance", { ...maintForm, cost: maintForm.cost ? parseFloat(maintForm.cost) : null });
      toast.success("Maintenance logged");
      const r = await api.get(`/inventory/vehicles/${activeVehicle.id}/maintenance`);
      setMaintLogs(r.data);
      setMaintForm({ vehicle_id: activeVehicle.id, description: "", cost: "" });
      load();
    } catch (e) { toast.error("Failed"); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Vehicles"
        subtitle="Fleet status + maintenance history"
        actions={<Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="vehicle-add-button"><Plus className="h-4 w-4 mr-2" /> Add vehicle</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add vehicle</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Ford, Isuzu…" data-testid="vehicle-name-input" /></div>
              <div><Label>Registration</Label><Input value={form.registration} onChange={(e) => setForm({...form, registration: e.target.value})} /></div>
              <div><Label>Model</Label><Input value={form.model} onChange={(e) => setForm({...form, model: e.target.value})} /></div>
              <div><Label>Year</Label><Input type="number" value={form.year} onChange={(e) => setForm({...form, year: e.target.value})} /></div>
              <div><Label>Status</Label><Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["operational","maintenance","out_of_service"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <DialogFooter><Button onClick={submit} disabled={saving} data-testid="vehicle-submit-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>}
      />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? <Card className="p-6 text-sm text-muted-foreground">Loading…</Card>
          : vehicles.length === 0 ? <Card className="col-span-3 p-12 text-center text-sm text-muted-foreground"><Truck className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />No vehicles yet.</Card>
          : vehicles.map((v) => (
            <Card key={v.id} className="rounded-xl hover:shadow-md transition-shadow cursor-pointer" onClick={() => openMaintenance(v)} data-testid={`vehicle-card-${v.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-baseline justify-between gap-2">
                  <CardTitle className="text-lg" data-testid={`vehicle-name-${v.id}`}>{v.name}</CardTitle>
                  <div className="flex items-center gap-1.5">
                    <StatusPill status={v.status} />
                    {canDelete && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={deletingId === v.id}
                            data-testid={`vehicle-delete-${v.id}`}
                            className="h-7 w-7 text-muted-foreground hover:text-destructive"
                            aria-label={`Delete ${v.name}`}
                          >
                            {deletingId === v.id
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : <Trash2 className="h-3.5 w-3.5" />}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete {v.name}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This permanently removes the vehicle <strong>and every maintenance log</strong> attached to it. The deletion is captured in the audit trail.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid={`vehicle-delete-cancel-${v.id}`}>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => remove(v)}
                              data-testid={`vehicle-delete-confirm-${v.id}`}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-xs text-muted-foreground space-y-1">
                  <div>{v.model || ""} {v.year ? `(${v.year})` : ""}</div>
                  <div className="font-mono">{v.registration || ""}</div>
                </div>
                <div className="mt-3 pt-3 border-t text-xs">
                  <div className="text-muted-foreground">Last service:</div>
                  <div className="mt-1">{v.last_maintenance_at ? <><span className="text-foreground">{safeDistanceToNow(v.last_maintenance_at)} ago</span>. <span className="text-muted-foreground">{v.last_maintenance_desc?.slice(0, 50)}…</span></> : ""}</div>
                </div>
              </CardContent>
            </Card>
          ))}
      </div>

      <Dialog open={maintOpen} onOpenChange={setMaintOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{activeVehicle?.name}. Maintenance</DialogTitle></DialogHeader>
          <div className="space-y-3 mb-4">
            <Textarea placeholder="What was done?" value={maintForm.description} onChange={(e) => setMaintForm({...maintForm, description: e.target.value})} data-testid="maint-description" />
            <div className="flex gap-2">
              <Input type="number" placeholder="Cost (optional)" value={maintForm.cost} onChange={(e) => setMaintForm({...maintForm, cost: e.target.value})} className="flex-1" />
              <Button onClick={submitMaint} disabled={saving} data-testid="maint-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Wrench className="h-4 w-4 mr-2" />Log</>}</Button>
            </div>
          </div>
          <div className="space-y-2 max-h-[300px] overflow-auto" data-testid="maint-history">
            {maintLogs.length === 0 ? <p className="text-sm text-muted-foreground">No maintenance logged yet.</p>
              : maintLogs.map((m) => (
                <div key={m.id} className="p-3 rounded-md border">
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm">{m.description}</span>
                    <span className="text-xs text-muted-foreground">{safeFormatDate(m.created_at, "MMM d, yyyy")}</span>
                  </div>
                  {m.cost && <div className="text-xs text-muted-foreground mt-1">Cost: {m.cost}</div>}
                </div>
              ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
